import type { Grade } from '../../api-models/Grade'
import type { Price } from '../../api-models/Price'
import type { MobilePlanOffer } from '../../api-specs-partnerships/mobile-plan/types/mobile-plan-offers'
import type { PaymentMethod } from '../../api-specs-payment/payment/payment.types'
import { createHttpEndpoint } from '../../utils/createHttpEndpoint'

/**
 * @see {@link https://devportal.backmarket.io/catalog/default/api/product-page-service-api/definition#/product-page/get_pickers}
 */
export const getPickers = createHttpEndpoint<GetPickersResponse>({
  method: 'GET',
  operationId: 'getPickers',
  path: '/product-page/products/:productId/pickers',
})

export type PickersResponseItem = {
  trackingValue?: string
  label: string
  sublabel?: string | null
  icon?: { url: string | null; name: string | null } | null
  highlight?: string
  selected: boolean
  defaultForMarket: boolean | null
  available: boolean
  acquirable: boolean
  price?: Price
  color: string | null
  goodDeal?: boolean
  bestDeal: boolean
  productId: string
  sortingKey?: number | string | null
  slug?: string
  parameters?: {
    grade: Grade
    specialOfferType: {
      name: string
      value: number
    }
    mobilePlanOfferId: string | null
  }
  tag?: {
    label: string
    type:
      | 'primary'
      | 'secondary'
      | 'info'
      | 'success'
      | 'warning'
      | 'danger'
      | 'alternate'
  } | null
  contextualCallout?: {
    icon: string
    text: string
    type: 'warning'
  } | null
  deals?: Deal[]
}

export type Operation = {
  type: 'NoOperation' | 'Subtract' | 'Add'
  price: Price | null
}

export type PickersResponseServiceItem = Omit<
  PickersResponseItem,
  'parameters' | 'color'
> & {
  parameters: Partial<PickersResponseItem['parameters']>
  operation: Operation
  // TODO improve typechecking here, as the first is only for mobile plan steps & the second for trade-in
  mobilePlanOffer?: MobilePlanOffer | null
  tradeInDiscount?: Price | null
}

export type Picker = {
  id: string
  trackingId?: string
  label: string
  sublabel?: string
  sectionTitle?: string
  items: Array<PickersResponseItem>
  guidanceCard?: {
    clickable: boolean
    text: string
    icon: {
      name: string | null
      url: string | null
    }
  }
  sideContentV2?: {
    type: string
    content: {
      grade?: number
      items?: {
        sectionId: string
        imageUrl: string
        title: string
        tags: {
          id: string
          text: string
          icon: string
        }[]
      }[]
      imageUrl?: string
    }[]
  }
}

export type PickerService = Omit<Picker, 'items'> & {
  items: Array<PickersResponseServiceItem>
}

export type PickerWithExtraData = PickersResponseServiceItem & {
  mobilePlanOffer: MobilePlanOffer
}

//  Extra data guard
export function hasItemExtraData(
  item: PickersResponseServiceItem,
): item is PickerWithExtraData {
  return Boolean(item.mobilePlanOffer)
}

type ShippingTime = {
  '@type': string
  maxValue: number
  minValue: number
  unitCode: string
}

type Shipping = {
  price: Price | null
  delayInHours: number
  earliestArrivalDate: string
  latestArrivalDate: string
  provider: string
  seo: {
    deliveryTime: {
      '@type': string
      cutoffTime: string
      handlingTime: ShippingTime
      transitTime: ShippingTime
    }
  }
}

// Variation of type 'Price' defining the 'amount' as a number.
// Recommendation is to use a string (as 'Price' does) but pickers API deals returned number.
export type PriceNumber = Omit<Price, 'amount'> & { amount: number }

type DealPercentage = {
  value: number
  minimumPrice: PriceNumber
}

export type HistoricalPriceDeal = {
  type: 'historical_price'
  referencePrice: PriceNumber
}

export type SellerFlashSalesDeal = {
  type: 'seller_flash_sales'
  percentage: DealPercentage
}

export type FlashSalesDeal = {
  type: 'flash_sales'
  discount: PriceNumber
  discountCode?: string | null
}

export type Deal = HistoricalPriceDeal | SellerFlashSalesDeal | FlashSalesDeal

export type GetPickersResponse = {
  productVariants: string[]
  isOutOfStock: boolean
  pickerGroups: Array<Picker>
  selectedOffer: null | {
    campaignId: string | null
    populationId: string | null
    offerId: string
    offerLegacyId: number | null
    paymentMethods: Array<PaymentMethod> | null
    price: Price | null
    defaultWarrantyDelay: string | null
    discount: null | {
      price: Price
      rate: string
    }
    shipping: null | {
      free?: Shipping
      express?: Shipping
    }
    sellerComment: string | null
    offerType: {
      name: string
      value: 0 | 7 | 9
    }
    grade: Grade
    newBattery?: {
      status: 'not-available' | 'available' | 'disabled'
      offerType?: number
    }
    battery9099?: {
      status: 'not-available' | 'available' | 'disabled'
      offerType?: number
    }
    stockLabel: string | null
    merchant: {
      merchantId: string
      city: string
      company: string
      country: {
        name: string
        code: string
      }
      state: string
      sellerSinceYear: string
      merchantUrl: string
    } | null
    deals?: Array<Deal>
  }
  upgrade: null | {
    productId: string
    grade: number
    specialOfferType: 0 | 7 | 9
    price: Price
    pickerFields: {
      [key: string]: { value: string; upgrade: boolean; color?: string }
    }
    image: string
  }
}

/**
 * `services/pickers` is being deprecated in favor of `services/mobile-plan-pickers`
 * and `services/trade-in-pickers` (PPI-7770) — this app no longer calls it directly.
 * This shape is now synthesized at runtime by merging those two responses (see
 * `toLegacyGroup` in `useFetchServicePickers.ts`), kept around for the legacy PDP
 * funnel tree (`CustomizationFunnel`, `createCustomizationFunnelData`,
 * `hasItemExtraData`, ...) that still consumes this merged shape. Tracked in
 * PPI-7874 to retype that tree and remove this shape.
 */
export type GetLegacyServicePickersResponse = {
  pickerGroups: Array<PickerService>
}
